import React, { useEffect, useState } from "react";
import { Link } from "gatsby";
import { IoMdArrowForward } from "@react-icons/all-files/io/IoMdArrowForward";
import { Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import { Container } from "~/atoms/Container";
import { PATH } from "~/constant/path";
import { colors } from "~/utils/colors";
import { breakPoints } from "~/utils/variables";
import { IndexCaseCard } from "~/molecules/IndexCaseCard";

type Props = {
  caseCards: GatsbyTypes.ContentfulCase[];
};

const CaseTrack: React.FC<Props> = ({ caseCards }) => (
  <>
    <div className="track">
      {caseCards.map((caseCard) => (
        <div key={caseCard.contentful_id || caseCard.id} className="slide">
          <IndexCaseCard caseCard={caseCard} />
        </div>
      ))}
    </div>

    <style jsx>{`
      .track {
        display: flex;
        flex: none;
        align-items: stretch;
        animation: caseScroll 40s linear infinite;
      }
      .slide {
        display: flex;
        flex: none;
        width: 320px;
        margin-right: 20px;
      }
      @keyframes caseScroll {
        0% {
          transform: translateX(0);
        }
        100% {
          transform: translateX(-100%);
        }
      }
      @media (${breakPoints.tablet}) {
        .slide {
          width: 300px;
        }
      }
    `}</style>
  </>
);

export const CasesOnTop: React.FC<Props> = ({ caseCards }) => {
  const [isSp, setIsSp] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 767px)");
    const apply = () => setIsSp(media.matches);
    apply();
    media.addEventListener("change", apply);
    return () => media.removeEventListener("change", apply);
  }, []);

  return (
    <>
      <div id="cases" className="anchor" />
      <section className="section">
        <Container>
          <div className="head">
            <h2 className="title">導入事例</h2>
            <p className="lead">Refcomeとともに取り組んだ企業の成功ストーリーをご紹介します。</p>
          </div>
        </Container>

        {!isSp ? (
          <div className="marquee">
            <CaseTrack caseCards={caseCards} />
            <CaseTrack caseCards={caseCards} />
          </div>
        ) : (
          <div className="mobileSlider">
            <Swiper
              modules={[Pagination]}
              slidesPerView={1}
              spaceBetween={16}
              pagination={{ clickable: true }}
              grabCursor
              watchOverflow
              observer
              observeParents
            >
              {caseCards.map((caseCard) => (
                <SwiperSlide key={caseCard.contentful_id || caseCard.id}>
                  <div className="mobileSlide">
                    <IndexCaseCard caseCard={caseCard} />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        )}

        <Container>
          <div className="moreWrap">
            <div className="more">
              <Link to={PATH.CASES.to}>
                導入事例記事を見る
                <IoMdArrowForward />
              </Link>
            </div>
          </div>
        </Container>
      </section>

      <style jsx>{`
        .anchor {
          display: block;
          height: 90px;
          margin-top: -90px;
          visibility: hidden;
        }

        .section {
          padding: 88px 0 96px;
          background: ${colors.white};
        }
        .section :global(.container) {
          display: block;
          width: 100%;
          max-width: 1000px;
          padding: 0 24px;
          box-sizing: border-box;
        }

        .head {
          text-align: center;
        }
        .title {
          margin: 0;
          color: ${colors.defaultText};
          font-size: 48px;
          font-weight: bold;
          line-height: 1.3;
          letter-spacing: 0.02em;
        }
        .lead {
          margin: 24px 0 0;
          color: ${colors.defaultText};
          font-size: 18px;
          line-height: 1.9;
        }

        .marquee {
          display: flex;
          overflow: hidden;
          width: 100%;
          margin-top: 44px;
          padding: 20px 0 40px;
        }
        .marquee:hover :global(.track) {
          animation-play-state: paused;
        }

        .mobileSlider {
          width: 100%;
          margin-top: 24px;
          padding: 8px 16px 4px;
          box-sizing: border-box;
        }
        .mobileSlider :global(.swiper) {
          width: 100%;
          padding-bottom: 36px;
        }
        .mobileSlider :global(.swiper-wrapper) {
          align-items: stretch;
        }
        .mobileSlider :global(.swiper-slide) {
          width: 100%;
          height: auto;
          box-sizing: border-box;
        }
        .mobileSlide {
          width: 100%;
          box-sizing: border-box;
          padding: 8px 2px 12px;
        }
        .mobileSlider :global(.swiper-pagination) {
          bottom: 0;
        }
        .mobileSlider :global(.swiper-pagination-bullet) {
          width: 8px;
          height: 8px;
          background: ${colors.gray4};
          opacity: 1;
        }
        .mobileSlider :global(.swiper-pagination-bullet-active) {
          background: ${colors.brand};
        }

        .moreWrap {
          display: flex;
          justify-content: center;
          margin-top: 16px;
        }
        .more :global(a) {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          min-height: 68px;
          padding: 18px 36px;
          border-radius: 999px;
          background: linear-gradient(90deg, #ec7f18 0%, #ec553f 100%);
          color: ${colors.whiteText};
          font-size: 18px;
          font-weight: bold;
          letter-spacing: 0.02em;
          text-decoration: none;
          box-shadow: 0 8px 18px rgba(236, 100, 40, 0.3);
          transition: transform 0.25s ease, box-shadow 0.25s ease, filter 0.25s ease;
        }
        .more :global(a:hover) {
          transform: translateY(-4px);
          filter: brightness(1.05);
          box-shadow: 0 14px 28px rgba(236, 100, 40, 0.4);
        }
        .more :global(svg) {
          width: 20px;
          height: 20px;
          transition: transform 0.25s ease;
        }
        .more :global(a:hover svg) {
          transform: translateX(4px);
        }

        @media (${breakPoints.tablet}) {
          .section {
            padding: 64px 0 72px;
          }
          .title {
            font-size: 40px;
          }
          .lead {
            font-size: 16px;
          }
          .marquee {
            margin-top: 32px;
          }
        }

        @media (${breakPoints.sp}) {
          .section {
            padding: 48px 0 56px;
          }
          .section :global(.container) {
            padding: 0 16px;
          }
          .title {
            font-size: 28px;
          }
          .lead {
            margin-top: 12px;
            font-size: 14px;
            line-height: 1.8;
          }
          .more :global(a) {
            width: 100%;
            max-width: 360px;
            min-height: 60px;
            padding: 16px 24px;
            font-size: 16px;
          }
        }
      `}</style>
    </>
  );
};
